import { ThemeEvents } from '@theme/events';

/**
 * Favicon Cart Badge
 * Displays cart item count as a red badge on the favicon
 * Following Shopify best practices for theme development
 */
class FaviconCartBadge {
  constructor() {
    this.canvas = null;
    this.context = null;
    /** @type {HTMLLinkElement | null} */
    this.originalFavicon = null;
    this.currentCount = 0;
    
    this.init();
  }

  /**
   * Initialize the favicon cart badge
   */
  init() {
    // Find and store the original favicon
    const faviconElement = document.querySelector('link[rel="icon"], link[rel="shortcut icon"]');
    this.originalFavicon = /** @type {HTMLLinkElement | null} */ (faviconElement);
    
    if (!this.originalFavicon) {
      // Create a default favicon if none exists
      this.originalFavicon = document.createElement('link');
      this.originalFavicon.rel = 'icon';
      this.originalFavicon.type = 'image/x-icon';
      document.head.appendChild(this.originalFavicon);
    }

    // Listen for cart update events
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate.bind(this));
    
    // Initialize cart count from session storage
    this.initializeCartCount();
  }

  /**
   * Initialize cart count from existing session storage
   */
  initializeCartCount() {
    try {
      const sessionStorageCount = sessionStorage.getItem('cart-count');
      if (sessionStorageCount) {
        const { value } = JSON.parse(sessionStorageCount);
        const count = parseInt(value, 10);
        if (count >= 0) {
          this.updateFavicon(count);
        }
      }
    } catch (error) {
      console.warn('Error reading cart count from session storage:', error);
    }
  }

  /**
   * Handle cart update events
   * @param {CustomEvent} event - Cart update event
   */
  handleCartUpdate(event) {
    const cartData = event.detail.data;
    
    if (!cartData) return;

    // Get the new cart count
    let newCount = 0;
    
    if (cartData.source === 'product-form-component') {
      // For product form submissions, add to current count
      const itemCount = cartData.itemCount || 0;
      newCount = this.currentCount + itemCount;
    } else {
      // For other sources, use the provided item count
      newCount = cartData.itemCount || 0;
    }

    this.updateFavicon(newCount);
  }

  /**
   * Update the favicon with cart count badge
   * @param {number} count - Number of items in cart
   */
  updateFavicon(count) {
    this.currentCount = count;
    
    if (count <= 0) {
      this.removeBadge();
      return;
    }

    this.drawFaviconWithBadge(count);
  }

  /**
   * Remove the badge and restore original favicon
   */
  removeBadge() {
    if (this.originalFavicon) {
      // Reset to original favicon URL or remove href to use default
      this.originalFavicon.href = this.getOriginalFaviconUrl() || '';
    }
  }

  /**
   * Get the original favicon URL
   * @returns {string} Original favicon URL
   */
  getOriginalFaviconUrl() {
    // Try to get from data attribute or use a default
    const originalUrl = this.originalFavicon?.dataset?.originalHref;
    if (originalUrl) return originalUrl;
    
    // Common Shopify favicon path
    return '/favicon.ico';
  }

  /**
   * Draw favicon with cart count badge
   * @param {number} count - Number of items in cart
   */
  drawFaviconWithBadge(count) {
    // Create canvas if it doesn't exist
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvas.width = 32;
      this.canvas.height = 32;
      this.context = this.canvas.getContext('2d');
    }

    const size = 32;
    const ctx = this.context;
    
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Load and draw the original favicon
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      if (!ctx || !this.canvas) return;
      // Draw the original favicon
      ctx.drawImage(img, 0, 0, size, size);
      
      // Draw the red badge
      this.drawBadge(ctx, count, size);
      
      // Update the favicon
      this.updateFaviconHref(this.canvas.toDataURL());
    };

    img.onerror = () => {
      if (!ctx || !this.canvas) return;
      // If image fails to load, draw a simple icon
      this.drawDefaultIcon(ctx, size);
      this.drawBadge(ctx, count, size);
      this.updateFaviconHref(this.canvas.toDataURL());
    };

    // Store original href if not already stored
    if (this.originalFavicon && !this.originalFavicon.dataset.originalHref) {
      this.originalFavicon.dataset.originalHref = this.originalFavicon.href || this.getOriginalFaviconUrl();
    }

    img.src = this.getOriginalFaviconUrl();
  }

  /**
   * Draw a default icon when original favicon can't be loaded
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {number} size - Icon size
   */
  drawDefaultIcon(ctx, size) {
    // Draw a simple shopping bag icon
    ctx.fillStyle = '#8542ff'; // Shopify purple
    ctx.fillRect(0, 0, size, size);
    
    // Draw a simple shopping bag shape
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(8, 12, 16, 16);
    ctx.fillRect(10, 8, 4, 8);
    ctx.fillRect(18, 8, 4, 8);
  }

  /**
   * Draw the red badge with count
   * @param {CanvasRenderingContext2D} ctx - Canvas context
   * @param {number} count - Number of items
   * @param {number} size - Canvas size
   */
  drawBadge(ctx, count, size) {
    const badgeSize = Math.min(20, size * 0.75);
    const badgeX = size - badgeSize;
    const badgeY = 0;

    // Draw red circle
    ctx.fillStyle = '#ff0000';
    ctx.beginPath();
    ctx.arc(badgeX + badgeSize/2, badgeY + badgeSize/2, badgeSize/2, 0, 2 * Math.PI);
    ctx.fill();

    // Draw white text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Limit display to 99+ for very high counts
    const displayCount = count > 99 ? '99+' : count.toString();
    
    ctx.fillText(
      displayCount, 
      badgeX + badgeSize/2, 
      badgeY + badgeSize/2
    );
  }

  /**
   * Update the favicon href with new data URL
   * @param {string} dataUrl - Data URL of the new favicon
   */
  updateFaviconHref(dataUrl) {
    if (this.originalFavicon) {
      this.originalFavicon.href = dataUrl;
    }
  }
}

// Initialize the favicon cart badge when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FaviconCartBadge();
  });
} else {
  new FaviconCartBadge();
}

export default FaviconCartBadge;
